<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <!-- ====== EN-TÊTE ====== -->
    <div
      class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
      <div class="d-flex align-items-center flex-wrap gap-2">
        <h5 class="text-black fw-semibold mb-0">
          Session de caisse<span v-if="rapport"> — {{ rapport.session.caisse }}</span>
        </h5>
        <span v-if="rapport" class="badge fs-13" :class="estFermee ? 'bg-danger' : 'bg-success'">
          {{ estFermee ? 'Fermée' : 'Ouverte (en cours)' }}
        </span>
      </div>
      <div class="d-print-none d-flex gap-2 mt-2 mt-lg-0">
        <!-- <button type="button" class="btn btn-outline-primary" @click="imprimer" :disabled="!rapport">
          <i class="fa fa-print me-1"></i>Imprimer
        </button> -->
        <router-link to="/OuverturesFermetures" class="btn btn-primary">Retour à la liste</router-link>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-25">
      <div v-if="chargement" class="text-center text-muted fs-md-14 py-4">Chargement de la session...</div>
      <div v-else-if="!rapport" class="alert alert-danger mb-0">
        Impossible de charger les détails de cette session.
      </div>

      <template v-else>
        <!-- ====== 1. INFORMATIONS GÉNÉRALES ====== -->
        <h6 class="section-titre">Informations générales</h6>
        <div class="row g-3 mb-4">
          <div class="col-md-6">
            <div class="info-bloc h-100">
              <h6 class="fw-bold mb-2">Ouverture</h6>
              <div class="info-ligne"><span>Date</span><strong>{{ format_date(rapport.session.dateOuverture) }}</strong></div>
              <div class="info-ligne"><span>Ouverte par</span><strong>{{ rapport.session.ouvertPar || '—' }}</strong></div>
              <div class="info-ligne">
                <span>Personnes présentes</span>
                <strong>{{ listePersonnes(rapport.session.personnesOuverture) }}</strong>
              </div>
              <div class="info-ligne"><span>Observation</span><strong>{{ rapport.session.observations || 'Aucune' }}</strong></div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="info-bloc h-100">
              <h6 class="fw-bold mb-2">Fermeture</h6>
              <template v-if="estFermee">
                <div class="info-ligne"><span>Date</span><strong>{{ format_date(rapport.session.dateFin) }}</strong></div>
                <div class="info-ligne">
                  <span>Personnes présentes</span>
                  <strong>{{ listePersonnes(rapport.session.personnesFermeture) }}</strong>
                </div>
                <div class="info-ligne"><span>Observation</span><strong>{{ rapport.session.observationsFin || 'Aucune' }}</strong></div>
              </template>
              <p v-else class="text-muted mb-0">La session est toujours ouverte.</p>
            </div>
          </div>
        </div>

        <!-- ====== 2. POINT GÉNÉRAL ====== -->
        <h6 class="section-titre">Point général</h6>
        <div class="row g-3" :class="estFermee ? 'mb-3' : 'mb-4'">
          <div class="col-6 col-lg-3">
            <div class="kpi kpi-neutre">
              <div class="kpi-label">Fond de caisse</div>
              <div class="kpi-value">{{ separateur(rapport.fondDeCaisse) }}</div>
            </div>
          </div>
          <div class="col-6 col-lg-3">
            <div class="kpi kpi-entree">
              <div class="kpi-label">Entrées</div>
              <div class="kpi-value text-success">+ {{ separateur(rapport.totaux.entrees) }}</div>
            </div>
          </div>
          <div class="col-6 col-lg-3">
            <div class="kpi kpi-sortie">
              <div class="kpi-label">Sorties</div>
              <div class="kpi-value text-danger">− {{ separateur(rapport.totaux.sorties) }}</div>
            </div>
          </div>
          <div class="col-6 col-lg-3">
            <div class="kpi kpi-theorique">
              <div class="kpi-label">{{ estFermee ? 'Solde théorique' : 'Solde actuel' }}</div>
              <div class="kpi-value">{{ separateur(rapport.soldeTheorique) }}</div>
            </div>
          </div>
        </div>

        <div v-if="estFermee" class="info-bloc mb-4">
          <h6 class="fw-bold mb-2">Comptage à la fermeture</h6>
          <div class="info-ligne"><span>Solde théorique</span><strong>{{ separateur(rapport.soldeTheorique) }}</strong></div>
          <div class="info-ligne"><span>Solde compté</span><strong>{{ separateur(rapport.soldeCompte ?? 0) }}</strong></div>
          <strong :class="rapport.ecart === 0 ? 'text-success' : 'text-danger'">
            {{ formatEcart(rapport.ecart ?? 0) }}
          </strong>
          <p class="text-muted mb-0 mt-2">{{ messageEcart }}</p>
        </div>

        <!-- ====== 3. MOUVEMENTS DE CAISSE ====== -->
        <h6 class="section-titre">
          Mouvements de caisse
          <span class="text-muted fw-normal">({{ nbMouvements }})</span>
        </h6>
        <div class="info-bloc">
          <div v-if="groupes.length === 0" class="text-muted py-3">Aucun mouvement enregistré.</div>
          <div v-else class="table-responsive journal-scroll">
            <table class="table table-sm align-middle mb-0 text-nowrap">
              <thead class="table-light">
                <tr>
                  <th>Date et heure</th>
                  <th>Opération</th>
                  <th>Détail</th>
                  <th>Caissier</th>
                  <th class="text-end">Montant</th>
                  <th class="text-end">Solde après</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="g in groupes" :key="g.cle">
                  <tr
                    v-for="(m, i) in g.lignes"
                    :key="m.id"
                    :class="{ 'groupe-debut': i === 0, 'groupe-lie': g.lignes.length > 1 }">
                    <td>{{ formatDateHeure(m.date) }}</td>
                    <td>{{ libelleType(m.type) }}</td>
                    <td class="text-wrap detail-cell">{{ m.motif || m.refCommande || '—' }}</td>
                    <td>{{ m.caissier || '—' }}</td>
                    <td class="text-end fw-semibold" :class="m.sens === 'ENTREE' ? 'text-success' : 'text-danger'">
                      {{ m.sens === 'ENTREE' ? '+' : '−' }} {{ separateur(m.montant) }}
                    </td>
                    <td class="text-end">{{ separateur(m.soldeApres) }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import ApiService from "@/services/ApiService";
import { error, format_date, separateur } from "@/utils/utils";

interface Mouvement {
  id: number;
  date: string;
  type: string;
  sens: "ENTREE" | "SORTIE";
  montant: number;
  refCommande: string | null;
  commandeClientId: number | null;
  motif: string | null;
  caissier: string | null;
  soldeApres: number;
}

interface RapportSession {
  session: {
    id: number;
    caisse: string;
    status: "OUVERTE" | "EN_COURS" | "FERMER";
    dateOuverture: string;
    dateFin: string | null;
    observations: string | null;
    observationsFin: string | null;
    ouvertPar: string | null;
    personnesOuverture: (string | null)[];
    personnesFermeture: (string | null)[];
  };
  fondDeCaisse: number;
  totaux: { entrees: number; sorties: number };
  soldeTheorique: number;
  soldeCompte: number | null;
  ecart: number | null;
  mouvements: Mouvement[];
}

// Un groupe = les mouvements d'une même vente (encaissement, monnaie rendue, remboursement)
interface Groupe {
  cle: string;
  lignes: Mouvement[];
}

const LIBELLES_TYPES: Record<string, string> = {
  ENCAISSEMENT_VENTE: "Montant reçu d'une vente",
  RENDU_MONNAIE: "Monnaie rendue",
  REMBOURSEMENT_ANNULATION: "Remboursement (annulation)",
  ENCAISSEMENT_CREANCE: "Règlement de créance",
  APPORT: "Apport",
  RETRAIT: "Retrait",
  DEPENSE: "Dépense",
  CORRECTION: "Correction",
};

export default defineComponent({
  name: "ViewOuvFerm",
  setup() {
    const route = useRoute();
    const rapport = ref<RapportSession | null>(null);
    const chargement = ref(true);

    const getRapport = async () => {
      const ouvFerId = route.params.id as string;
      if (!ouvFerId) {
        chargement.value = false;
        error("ID d'ouverture/fermeture manquant.");
        return;
      }
      try {
        const { data } = await ApiService.get(`/ouvfers/rapport?ouvFerId=${ouvFerId}`);
        rapport.value = data.data || null;
      } catch (err: any) {
        rapport.value = null;
        error(err.response?.data?.message || "Erreur lors de la récupération de la session.");
      } finally {
        chargement.value = false;
      }
    };

    const estFermee = computed(() => rapport.value?.session.status === "FERMER");
    const formatEcart = (e: number) => `${e > 0 ? "+ " : e < 0 ? "− " : ""}${separateur(Math.abs(e))}`;

    const messageEcart = computed(() => {
      const e = rapport.value?.ecart;
      if (e === null || e === undefined) return "";
      if (e === 0) return "La caisse est juste.";
      return e > 0 ? "Excédent en caisse." : "Manquant en caisse.";
    });

    // Regroupe les mouvements d'une même vente ; les autres restent isolés.
    // L'ordre chronologique est conservé (un groupe prend la place de son premier mouvement).
    const groupes = computed<Groupe[]>(() => {
      const resultat: Groupe[] = [];
      const index = new Map<string, Groupe>();
      for (const m of rapport.value?.mouvements ?? []) {
        const cle = m.commandeClientId ? `vente-${m.commandeClientId}` : `mvt-${m.id}`;
        let g = index.get(cle);
        if (!g) {
          g = { cle, lignes: [] };
          index.set(cle, g);
          resultat.push(g);
        }
        g.lignes.push(m);
      }
      return resultat;
    });

    const nbMouvements = computed(() => rapport.value?.mouvements?.length ?? 0);

    const libelleType = (type: string) => LIBELLES_TYPES[type] ?? type;

    const listePersonnes = (noms: (string | null)[]) => {
      const liste = (noms ?? []).filter(Boolean);
      return liste.length > 0 ? liste.join(", ") : "—";
    };

    const formatDateHeure = (d: string) =>
      d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

    const imprimer = () => window.print();

    onMounted(getRapport);

    return {
      rapport,
      chargement,
      estFermee,
      messageEcart,
      groupes,
      nbMouvements,
      libelleType,
      listePersonnes,
      formatDateHeure,
      imprimer,
      format_date,
      separateur,
      formatEcart
    };
  },
});
</script>

<style scoped>
.card {
  margin-bottom: 25px;
}

.card-head {
  padding: 15px 20px;
}

.section-titre {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 12px;
}

/* Blocs d'information */
.info-bloc {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fff;
}

.info-ligne {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 0;
  border-bottom: 1px dashed #eef0f2;
}

.info-ligne:last-child {
  border-bottom: none;
}

.info-ligne span {
  color: #6c757d;
}

.info-ligne strong {
  text-align: right;
  color: #212529;
}

/* Cartes de chiffres clés */
.kpi {
  border: 1px solid #e9ecef;
  border-left-width: 4px;
  border-radius: 8px;
  padding: 12px 16px;
  height: 100%;
  background: #fff;
}

.kpi-label {
  font-size: 13px;
  color: #6c757d;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  color: #212529;
}

.kpi-neutre { border-left-color: #adb5bd; }
.kpi-entree { border-left-color: #28a745; }
.kpi-sortie { border-left-color: #dc3545; }
.kpi-theorique { border-left-color: #0a59a4; }

/* Liste des mouvements */
.journal-scroll {
  max-height: 480px;
  overflow-y: auto;
}

.detail-cell {
  max-width: 280px;
}

/* Mouvements liés (même vente) : barre à gauche + fond léger, séparés des autres par un trait */
.groupe-debut td {
  border-top: 2px solid #dee2e6;
}

.groupe-lie td {
  background: #f6f8fb;
}

.groupe-lie td:first-child {
  border-left: 3px solid #0a59a4;
}

@media print {
  .journal-scroll {
    max-height: none;
    overflow: visible;
  }
}
</style>